import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  RiCameraLine,
  RiLockLine,
  RiShieldLine,
  RiUserLine,
  RiMailLine,
  RiPhoneLine,
  RiMapPinLine,
  RiCalendarLine,
  RiShoppingCartLine,
  RiFileListLine,
} from 'react-icons/ri';
import { GiTakeMyMoney } from 'react-icons/gi';
import { FaMoneyBillTransfer } from 'react-icons/fa6';
import { MdPayments, MdSpeed } from 'react-icons/md';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { profileService } from '../services';
import type { ProfileData, ProfileUpdateData } from '../types';
import { Card, Button, Modal, LoaderComponent } from '../components/ui';

// Format currency helper
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-CV', {
    style: 'currency',
    currency: 'CVE',
    minimumFractionDigits: 0,
  }).format(value);
};

// Format date helper
const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('pt-CV', {
    timeZone: 'Atlantic/Cape_Verde',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { showSuccess, showError } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [disableModalOpen, setDisableModalOpen] = useState(false);

  const [formData, setFormData] = useState<ProfileUpdateData>({
    nome: '',
    email: '',
    telemovel: '',
    morada: '',
  });

  const [passwordData, setPasswordData] = useState({
    senhaAtual: '',
    novaSenha: '',
    confirmarSenha: '',
  });

  const CONTADOR_REF_KEY = 'contador_prepago_ref';
  const [contadorRef, setContadorRef] = useState(() => localStorage.getItem(CONTADOR_REF_KEY) || '');
  const [contadorModalOpen, setContadorModalOpen] = useState(false);
  const [contadorInput, setContadorInput] = useState('');

  useEffect(() => {
    sessionStorage.setItem('pageTitle', 'Perfil');
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const data = await profileService.getProfile();
      setProfile(data);
      const tel = data.telefone
        || data.contactos?.find((c: any) => (c.tipo || '').toUpperCase().includes('TELEM'))?.contacto
        || '';
      setFormData({
        nome: data.nome || '',
        email: data.email || '',
        telemovel: tel,
        morada: data.morada || '',
      });
    } catch (err) {
      showError(err instanceof Error ? err.message : t('profile.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof ProfileUpdateData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await profileService.updateProfile(formData);
      showSuccess(t('profile.profileUpdated'));
      setEditing(false);
      loadProfile();
    } catch (err) {
      showError(err instanceof Error ? err.message : t('profile.errorUpdatingProfile'));
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = (reader.result as string).split(',')[1];
      const mimeType = file.type;

      try {
        await profileService.uploadAvatar({ base64, mimeType });
        showSuccess(t('profile.avatarUpdated'));
        loadProfile();
      } catch (err) {
        showError(err instanceof Error ? err.message : t('profile.errorUpdatingAvatar'));
      }
    };
    reader.readAsDataURL(file);
  };

  const handlePasswordChange = async () => {
    if (passwordData.novaSenha !== passwordData.confirmarSenha) {
      showError(t('profile.passwordsDontMatch'));
      return;
    }

    if (passwordData.novaSenha.length < 8) {
      showError(t('profile.passwordMinLength'));
      return;
    }

    try {
      await profileService.changePassword(passwordData.senhaAtual, passwordData.novaSenha);
      showSuccess(t('profile.passwordChanged'));
      setPasswordModalOpen(false);
      setPasswordData({ senhaAtual: '', novaSenha: '', confirmarSenha: '' });
    } catch (err) {
      showError(err instanceof Error ? err.message : t('profile.errorChangingPassword'));
    }
  };

  const handleDisableAccount = async () => {
    try {
      await profileService.disableAccount();
      showSuccess(t('profile.accountDisabled'));
      await logout();
      navigate('/');
    } catch (err) {
      showError(err instanceof Error ? err.message : t('profile.errorDisabling'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoaderComponent size="lg" text={t('profile.loadingProfile')} />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <p className="text-pagali-error mb-4">{t('profile.errorLoading')}</p>
        <Button onClick={loadProfile}>{t('common.tryAgain')}</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Profile Header Card — business-card style */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-pagali-purple via-purple-400 to-yellow-500 shadow-lg">
        {/* Decorative mesh overlay */}
        <svg className="absolute inset-0 w-full h-full opacity-15" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="mesh" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
              <circle cx="30" cy="30" r="28" fill="none" stroke="white" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#mesh)" />
        </svg>

        <div className="relative flex flex-col md:flex-row items-center md:items-end gap-6 p-6 md:p-8">
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            {profile.avatar ? (
              <img
                src={profile.avatar}
                alt={profile.nome}
                className="w-28 h-28 rounded-2xl object-cover border-4 border-white/30 shadow-lg"
              />
            ) : (
              <div className="w-28 h-28 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-white text-4xl font-bold shadow-lg border-4 border-white/30">
                {profile.nome?.charAt(0).toUpperCase() || 'U'}
              </div>
            )}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="absolute -bottom-1 -right-1 w-8 h-8 bg-white text-pagali-primary rounded-full flex items-center justify-center shadow-md hover:scale-110 transition-transform"
            >
              <RiCameraLine className="text-sm" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              className="hidden"
            />
          </div>

          {/* Profile Info — label : value rows */}
          <div className="flex-1 flex flex-col items-center md:items-center gap-1.5 text-white text-sm">
            <div className="flex gap-2">
              <span className="font-semibold text-white/80 text-right min-w-[110px]">{t('profile.pagaliCode')} :</span>
              <span className="font-medium">{profile.codigo || '-'}</span>
            </div>
            <div className="flex gap-2">
              <span className="font-semibold text-white/80 text-right min-w-[110px]">{t('common.name')} :</span>
              <span className="font-medium">{profile.nome}</span>
            </div>
            {profile.pais && (
              <div className="flex gap-2">
                <span className="font-semibold text-white/80 text-right min-w-[110px]">{t('profile.country')} :</span>
                <span className="font-medium">{profile.pais}</span>
              </div>
            )}
            <div className="flex gap-2">
              <span className="font-semibold text-white/80 text-right min-w-[110px]">{t('common.email')} :</span>
              <span className="font-medium">{profile.email}</span>
            </div>
            {(profile.telefone || (profile.contactos && profile.contactos.length > 0)) && (
              <div className="flex gap-2">
                <span className="font-semibold text-white/80 text-right min-w-[110px]">{t('profile.phone')} :</span>
                <span className="font-medium">{profile.telefone || profile.contactos?.[0]?.contacto}</span>
              </div>
            )}
            <div className="flex gap-2">
              <span className="font-semibold text-white/80 text-right min-w-[110px]">{t('profile.address')} :</span>
              <span className="font-medium">{profile.morada || '-'}</span>
            </div>
          </div>

          {/* Edit Button — bottom right */}
          <div className="flex-shrink-0 self-end">
            <button
              onClick={() => (editing ? handleSaveProfile() : setEditing(true))}
              disabled={saving}
              className="px-6 py-2 bg-pagali-primary text-white text-sm font-semibold rounded-xl hover:bg-pagali-primary-dark transition-colors shadow-md"
            >
              {saving ? t('common.processing') : editing ? t('profile.save') : t('profile.edit')}
            </button>
            {editing && (
              <button
                onClick={() => setEditing(false)}
                className="ml-2 px-4 py-2 bg-white/20 text-white text-sm font-medium rounded-xl hover:bg-white/30 transition-colors"
              >
                {t('common.cancel')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Pagamentos Stats */}
        <Card>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center">
              <MdPayments className="text-2xl text-purple-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-pagali-gray-500">{t('profile.paymentsLabel')}</p>
              <p className="text-xl font-bold text-pagali-gray-800">
                {formatCurrency(profile.estatisticas?.pagamentos?.montante || 0)}
              </p>
              <p className="text-xs text-pagali-gray-400">
                {profile.estatisticas?.pagamentos?.transacoes || 0} {t('profile.transactionsLabel')}
              </p>
            </div>
            <GiTakeMyMoney className="text-3xl text-purple-200" />
          </div>
        </Card>

        {/* Compras Stats */}
        <Card>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-green-100 flex items-center justify-center">
              <RiShoppingCartLine className="text-2xl text-green-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-pagali-gray-500">{t('profile.purchasesLabel')}</p>
              <p className="text-xl font-bold text-pagali-gray-800">
                {formatCurrency(profile.estatisticas?.compras?.montante || 0)}
              </p>
              <p className="text-xs text-pagali-gray-400">
                {profile.estatisticas?.compras?.transacoes || 0} {t('profile.ordersLabel')}
              </p>
            </div>
            <FaMoneyBillTransfer className="text-3xl text-green-200" />
          </div>
        </Card>
      </div>

      {/* Profile Details / Edit Form */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Info */}
        <Card>
          <h3 className="text-lg font-semibold text-pagali-gray-800 mb-4 flex items-center gap-2">
            <RiUserLine className="text-pagali-primary" />
            {t('profile.personalInfo')}
          </h3>

          <div className="space-y-4">
            {editing ? (
              <>
                <div className="form-group">
                  <label className="input-label">{t('profile.fullName')}</label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={handleInputChange('nome')}
                    className="input-field"
                    placeholder={t('profile.fullNamePlaceholder')}
                  />
                </div>
                <div className="form-group">
                  <label className="input-label">{t('common.email')}</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    className="input-field"
                    placeholder={t('profile.emailPlaceholder')}
                  />
                </div>
                <div className="form-group">
                  <label className="input-label">{t('profile.phone')}</label>
                  <input
                    type="tel"
                    value={formData.telemovel}
                    onChange={handleInputChange('telemovel')}
                    className="input-field"
                    placeholder="+238 ..."
                  />
                </div>
                <div className="form-group">
                  <label className="input-label">{t('profile.address')}</label>
                  <textarea
                    value={formData.morada}
                    onChange={handleInputChange('morada')}
                    className="input-field"
                    rows={3}
                    placeholder={t('profile.addressPlaceholder')}
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button onClick={handleSaveProfile} loading={saving}>
                    {t('profile.save')}
                  </Button>
                  <Button variant="ghost" onClick={() => setEditing(false)}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <InfoRow icon={<RiUserLine />} label={t('common.name')} value={profile.nome} />
                <InfoRow icon={<RiMailLine />} label={t('common.email')} value={profile.email} />
                <InfoRow icon={<RiPhoneLine />} label={t('profile.phone')} value={profile.telefone || profile.contactos?.find((c: any) => (c.tipo || '').toUpperCase().includes('TELEM'))?.contacto || '-'} />
                <InfoRow icon={<RiUserLine />} label={t('profile.username')} value={profile.username} />
                <InfoRow icon={<RiMapPinLine />} label={t('profile.address')} value={profile.morada || '-'} />
                <InfoRow icon={<RiCalendarLine />} label={t('profile.memberSince')} value={formatDate(profile.dataRegisto)} />
                {profile.pais && <InfoRow icon={<RiMapPinLine />} label={t('profile.country')} value={profile.pais} />}
                {profile.tipoCliente && <InfoRow icon={<RiUserLine />} label={t('profile.type')} value={profile.tipoCliente} />}
              </>
            )}
          </div>
        </Card>

        {/* Right Column — Quick Access + Contacts + Documents */}
        <div className="space-y-6">
          {/* Quick Access Cards */}
          <Card>
            <h3 className="text-lg font-semibold text-pagali-gray-800 mb-4 flex items-center gap-2">
              <RiFileListLine className="text-pagali-primary" />
              {t('profile.myActivity')}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <QuickAccessCard
                icon="💳"
                label={t('profile.myCards')}
                onClick={() => navigate('/wallet')}
                color="bg-blue-50 hover:bg-blue-100"
              />
              <QuickAccessCard
                icon="🎫"
                label={t('profile.myTickets')}
                onClick={() => navigate('/meus-bilhetes')}
                color="bg-purple-50 hover:bg-purple-100"
              />
              <QuickAccessCard
                icon="🎟️"
                label={t('profile.myVouchers')}
                onClick={() => navigate('/vouchers')}
                color="bg-amber-50 hover:bg-amber-100"
              />
              <QuickAccessCard
                icon="🛒"
                label={t('profile.myPurchases')}
                onClick={() => navigate('/ecommerce/orders')}
                color="bg-green-50 hover:bg-green-100"
              />
              <QuickAccessCard
                icon="🚚"
                label={t('profile.myDeliveries')}
                onClick={() => navigate('/food/orders')}
                color="bg-orange-50 hover:bg-orange-100"
              />
              <QuickAccessCard
                icon="❤️"
                label={t('profile.myDonations')}
                onClick={() => navigate('/doacoes')}
                color="bg-pink-50 hover:bg-pink-100"
              />
            </div>
          </Card>

          {/* Contacts */}
          {profile.contactos && profile.contactos.length > 0 && (
            <Card>
              <h3 className="text-lg font-semibold text-pagali-gray-800 mb-4 flex items-center gap-2">
                <RiPhoneLine className="text-pagali-primary" />
                {t('profile.contacts')}
              </h3>
              <div className="space-y-3">
                {profile.contactos.map((contacto) => (
                  <div
                    key={contacto.id}
                    className="flex items-center justify-between p-3 bg-pagali-gray-50 rounded-xl"
                  >
                    <span className="text-sm text-pagali-gray-500">{contacto.tipo}</span>
                    <span className="font-medium text-pagali-gray-800">{contacto.contacto}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Documents */}
          {profile.documentos && profile.documentos.length > 0 && (
            <Card>
              <h3 className="text-lg font-semibold text-pagali-gray-800 mb-4 flex items-center gap-2">
                <RiFileListLine className="text-pagali-primary" />
                {t('profile.documents')}
              </h3>
              <div className="space-y-3">
                {profile.documentos.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 bg-pagali-gray-50 rounded-xl"
                  >
                    <div>
                      <span className="text-sm text-pagali-gray-500">{doc.tipo}</span>
                      {doc.tag && <span className="ml-2 text-xs px-2 py-0.5 bg-pagali-primary/10 text-pagali-primary rounded-full">{doc.tag}</span>}
                    </div>
                    <span className="font-medium text-pagali-gray-800">{doc.numero}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Pagamentos Rápidos */}
      <Card>
        <h3 className="text-lg font-semibold text-pagali-gray-800 mb-4 flex items-center gap-2">
          <MdSpeed className="text-pagali-primary" />
          Pagamentos Rápidos
        </h3>
        <button
          onClick={() => {
            setContadorInput(contadorRef);
            setContadorModalOpen(true);
          }}
          className="flex items-center gap-3 w-full p-4 rounded-xl border border-pagali-gray-200 hover:bg-pagali-gray-50 transition-colors text-left"
        >
          <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
            <MdSpeed className="text-xl text-blue-600" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-pagali-gray-800">Contador Pré-pago</p>
            <p className="text-sm text-pagali-gray-500">
              {contadorRef ? `Ref: ${contadorRef}` : 'Não configurado — toque para adicionar'}
            </p>
          </div>
          {contadorRef && (
            <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full font-medium">
              Guardado
            </span>
          )}
        </button>
      </Card>

      {/* Security Section */}
      <Card>
        <h3 className="text-lg font-semibold text-pagali-gray-800 mb-4 flex items-center gap-2">
          <RiLockLine className="text-pagali-primary" />
          {t('profile.security')}
        </h3>
        <div className="space-y-4">
          <button
            onClick={() => setPasswordModalOpen(true)}
            className="flex items-center gap-3 w-full p-4 rounded-xl border border-pagali-gray-200 hover:bg-pagali-gray-50 transition-colors text-left"
          >
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <RiLockLine className="text-xl text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-pagali-gray-800">{t('profile.changePassword')}</p>
              <p className="text-sm text-pagali-gray-500">{t('profile.changeAccessPassword')}</p>
            </div>
          </button>

          <button
            onClick={() => navigate('/kyc')}
            className="flex items-center gap-3 w-full p-4 rounded-xl border border-pagali-gray-200 hover:bg-pagali-gray-50 transition-colors text-left"
          >
            <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
              <RiShieldLine className="text-xl text-purple-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-pagali-gray-800">Verificação de Identidade (KYC)</p>
              <p className="text-sm text-pagali-gray-500">Verifica a tua identidade para aumentar os limites</p>
            </div>
          </button>

          <button
            onClick={() => setDisableModalOpen(true)}
            className="flex items-center gap-3 w-full p-4 rounded-xl border border-pagali-error/30 hover:bg-red-50 transition-colors text-left"
          >
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
              <span className="text-xl">&#9888;</span>
            </div>
            <div>
              <p className="font-medium text-pagali-error">{t('profile.disableAccount')}</p>
              <p className="text-sm text-pagali-gray-500">{t('profile.disableAccountWarning')}</p>
            </div>
          </button>
        </div>
      </Card>

      {/* Contador Ref Modal */}
      <Modal
        isOpen={contadorModalOpen}
        onClose={() => setContadorModalOpen(false)}
        title="Contador Pré-pago"
        footer={
          <div className="flex gap-3">
            {contadorRef && (
              <Button
                variant="ghost"
                onClick={() => {
                  localStorage.removeItem(CONTADOR_REF_KEY);
                  setContadorRef('');
                  setContadorModalOpen(false);
                }}
              >
                Remover
              </Button>
            )}
            <Button variant="ghost" onClick={() => setContadorModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                const ref = contadorInput.trim();
                if (ref.length >= 4) {
                  localStorage.setItem(CONTADOR_REF_KEY, ref);
                  setContadorRef(ref);
                  setContadorModalOpen(false);
                }
              }}
              disabled={contadorInput.trim().length < 4}
            >
              Guardar
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-pagali-gray-500">
            Guarde a referência do seu contador pré-pago para preenchimento automático no formulário de pagamento.
            Pode consultar a referência no portal Vinti4.
          </p>
          <div className="form-group">
            <label className="input-label">Referência do Contador</label>
            <input
              type="number"
              value={contadorInput}
              onChange={(e) => setContadorInput(e.target.value)}
              className="input-field"
              placeholder="Insira a referência do contador"
              autoFocus
            />
          </div>
        </div>
      </Modal>

      {/* Password Modal */}
      <Modal
        isOpen={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        title={t('profile.changePassword')}
        footer={
          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => setPasswordModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handlePasswordChange}>{t('profile.changePassword')}</Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="form-group">
            <label className="input-label">{t('profile.currentPassword')}</label>
            <input
              type="password"
              value={passwordData.senhaAtual}
              onChange={(e) => setPasswordData({ ...passwordData, senhaAtual: e.target.value })}
              className="input-field"
            />
          </div>
          <div className="form-group">
            <label className="input-label">{t('profile.newPassword')}</label>
            <input
              type="password"
              value={passwordData.novaSenha}
              onChange={(e) => setPasswordData({ ...passwordData, novaSenha: e.target.value })}
              className="input-field"
            />
          </div>
          <div className="form-group">
            <label className="input-label">{t('profile.confirmNewPassword')}</label>
            <input
              type="password"
              value={passwordData.confirmarSenha}
              onChange={(e) => setPasswordData({ ...passwordData, confirmarSenha: e.target.value })}
              className="input-field"
            />
          </div>
        </div>
      </Modal>

      {/* Disable Account Modal */}
      <Modal
        isOpen={disableModalOpen}
        onClose={() => setDisableModalOpen(false)}
        title={t('profile.disableAccount')}
        footer={
          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => setDisableModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="danger" onClick={handleDisableAccount}>
              {t('profile.disable')}
            </Button>
          </div>
        }
      >
        <p className="text-pagali-gray-600">
          {t('profile.disableAccountConfirm')}
        </p>
      </Modal>
    </div>
  );
};

// Quick Access Card Component
interface QuickAccessCardProps {
  icon: string;
  label: string;
  onClick: () => void;
  color: string;
}

const QuickAccessCard: React.FC<QuickAccessCardProps> = ({ icon, label, onClick, color }) => (
  <button
    onClick={onClick}
    className={`flex flex-col items-center gap-2 p-4 rounded-xl ${color} transition-colors cursor-pointer`}
  >
    <span className="text-2xl">{icon}</span>
    <span className="text-xs font-semibold text-pagali-gray-700 text-center leading-tight">{label}</span>
  </button>
);

// Info Row Component
interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

const InfoRow: React.FC<InfoRowProps> = ({ icon, label, value }) => (
  <div className="flex items-center gap-3 py-2 border-b border-pagali-gray-100 last:border-0">
    <span className="text-pagali-gray-400">{icon}</span>
    <span className="text-sm text-pagali-gray-500 w-24">{label}</span>
    <span className="flex-1 text-pagali-gray-800 font-medium">{value}</span>
  </div>
);

export default Profile;
